import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  clearRecentDramas,
  fetchMemberLibrary,
  fetchMemberPurchases,
  removeRecentDrama,
  setDramaFavorite,
} from '@/api/member';
import { mapDramaCard, mapLibraryRows, type CinemaSeries } from '@/utils/cinemaData';
import { ensureMemberAccessToken } from '@/utils/memberSession';

type LibraryView = 'recent' | 'favorites' | 'purchases';
type HistoryFilter = 'all' | 'unfinished' | 'finished';

const libraryViews: { id: LibraryView; icon: string; labelKey: string }[] = [
  { id: 'recent', icon: 'ri-time-line', labelKey: 'cinema_navHistory' },
  { id: 'favorites', icon: 'ri-bookmark-line', labelKey: 'cinema_favorites' },
  { id: 'purchases', icon: 'ri-shopping-bag-line', labelKey: 'cinema_myPurchases' },
];

export default function CinemaHistoryPage() {
  const { t, i18n } = useTranslation('cinema');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = searchParams.get('tab') as LibraryView;
  const [activeView, setActiveView] = useState<LibraryView>(
    ['favorites', 'purchases'].includes(initialView) ? initialView : 'recent',
  );
  const [activeFilter, setActiveFilter] = useState<HistoryFilter>('all');
  const [recent, setRecent] = useState<CinemaSeries[]>([]);
  const [favorites, setFavorites] = useState<CinemaSeries[]>([]);
  const [purchases, setPurchases] = useState<CinemaSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    const nextView = searchParams.get('tab') as LibraryView;
    setActiveView(['favorites', 'purchases'].includes(nextView) ? nextView : 'recent');
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void ensureMemberAccessToken()
      .then(async (token) => {
        if (!token) {
          navigate('/login');
          return;
        }
        // Library is the primary data source for history and favorites. Do not let
        // an optional purchases request hide those rows when it happens to fail.
        const library = await fetchMemberLibrary(token);
        if (cancelled) return;
        const mapped = mapLibraryRows(library);
        setRecent(mapped.recent);
        setFavorites(mapped.favorites);
        void fetchMemberPurchases(token)
          .then((bought) => {
            if (!cancelled) setPurchases(bought.map(mapDramaCard));
          })
          .catch(() => {
            if (!cancelled) setPurchases([]);
          });
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [i18n.resolvedLanguage, navigate]);

  const rows = activeView === 'favorites' ? favorites : activeView === 'purchases' ? purchases : recent;
  const filteredRows = useMemo(() => {
    if (activeView !== 'recent' || activeFilter === 'all') return rows;
    return rows.filter((item) => {
      const finished = item.totalEpisodes > 0 && item.lastEpisodeNo >= item.totalEpisodes;
      return activeFilter === 'finished' ? finished : !finished;
    });
  }, [activeFilter, activeView, rows]);

  const switchView = (view: LibraryView) => {
    setActiveView(view);
    setActiveFilter('all');
    setSearchParams(view === 'recent' ? {} : { tab: view });
  };

  const handleRemove = (item: CinemaSeries) => {
    setRemovingId(item.id);
    void ensureMemberAccessToken()
      .then(async (token) => {
        if (!token) return;
        if (activeView === 'favorites') {
          await setDramaFavorite(Number(item.id), false, token);
          setFavorites((prev) => prev.filter((row) => row.id !== item.id));
          return;
        }
        await removeRecentDrama(Number(item.id), token);
        setRecent((prev) => prev.filter((row) => row.id !== item.id));
      })
      .catch(() => undefined)
      .finally(() => setRemovingId(null));
  };

  const handleClearAll = () => {
    if (activeView !== 'recent' || clearing || !window.confirm(t('cinema_clearAllConfirm'))) return;
    setClearing(true);
    void ensureMemberAccessToken()
      .then((token) => token ? clearRecentDramas(token) : Promise.reject(new Error('unauthorized')))
      .then(() => setRecent([]))
      .catch(() => undefined)
      .finally(() => setClearing(false));
  };

  const emptyKey = activeView === 'favorites'
    ? 'cinema_emptyFavorites'
    : activeView === 'purchases' ? 'cinema_emptyPurchases' : 'cinema_emptyHistory';

  return (
    <div className="pt-14 md:pt-16 pb-16 md:pb-20">
      <div className="max-w-7xl mx-auto px-4 md:px-10 lg:px-14">
        <div className="py-4 md:py-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg md:text-2xl font-bold font-heading text-foreground-50">{t('cinema_navHistory')}</h1>
            <p className="text-xs md:text-sm text-foreground-500 mt-1">{t('cinema_historyCount', { count: rows.length })}</p>
          </div>
          {activeView === 'recent' && rows.length > 0 && (
            <button onClick={handleClearAll} disabled={clearing} className="text-xs text-foreground-500 hover:text-red-400 disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap">
              {clearing ? t('cinema_loading') : t('cinema_clearAll')}
            </button>
          )}
        </div>

        <div className="flex gap-1 mb-4 overflow-x-auto scrollbar-hide">
          {libraryViews.map((view) => (
            <button key={view.id} onClick={() => switchView(view.id)} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${activeView === view.id ? 'bg-[#F19744] text-background-950' : 'bg-background-900/60 text-foreground-500 hover:text-foreground-300'}`}>
              <i className={view.icon}></i>{t(view.labelKey)}
            </button>
          ))}
        </div>

        {activeView === 'recent' && (
          <div className="flex items-center gap-1 mb-5 md:mb-6 bg-background-900/60 rounded-full p-1 w-fit">
            {([
              { id: 'all', label: t('cinema_historyAll') },
              { id: 'unfinished', label: t('cinema_historyUnfinished') },
              { id: 'finished', label: t('cinema_historyFinished') },
            ] as const).map((tab) => (
              <button key={tab.id} onClick={() => setActiveFilter(tab.id)} className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${activeFilter === tab.id ? 'bg-[#F19744] text-background-950' : 'text-foreground-500 hover:text-foreground-300'}`}>
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-24 text-foreground-500"><i className="ri-loader-4-line text-2xl animate-spin" /></div>
        ) : filteredRows.length === 0 ? (
          <div className="pt-12 min-h-[42vh] flex items-center justify-center">
            <div className="text-center px-6">
              <span className="w-20 h-20 flex items-center justify-center mx-auto mb-5 rounded-full bg-foreground-50/5"><i className="ri-inbox-line text-4xl text-foreground-600"></i></span>
              <p className="text-sm text-foreground-600 mb-6">{activeView === 'recent' && rows.length > 0 ? t('cinema_noHistoryFilter') : t(emptyKey)}</p>
              {rows.length === 0 && <button onClick={() => navigate('/')} className="px-6 py-2.5 rounded-full text-sm font-medium text-background-950 bg-[#F19744] hover:bg-[#F19744]/90 transition-colors cursor-pointer whitespace-nowrap">{t('cinema_navHome')}</button>}
            </div>
          </div>
        ) : (
          <div className="space-y-2 md:space-y-3">
            {filteredRows.map((item) => {
              const finished = item.totalEpisodes > 0 && item.lastEpisodeNo >= item.totalEpisodes;
              const progress = item.totalEpisodes > 0 ? Math.min(100, Math.round((item.lastEpisodeNo / item.totalEpisodes) * 100)) : 0;
              const canRemove = activeView !== 'purchases';
              return (
                <div key={item.id} className={`transition-all duration-300 ${removingId === item.id ? 'opacity-0 -translate-x-4' : 'opacity-100 translate-x-0'}`}>
                  <button onClick={() => navigate(`/watch/${item.id}`)} className="w-full flex items-center gap-3 md:gap-4 p-3 md:p-4 rounded-xl bg-background-900/60 hover:bg-background-900/90 transition-all cursor-pointer text-left group">
                    <div className="relative w-24 md:w-32 h-16 md:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-background-800">
                      <img src={item.coverUrl} alt={item.title} className="w-full h-full object-cover" />
                      {activeView === 'recent' && !finished && <div className="absolute bottom-0 left-0 right-0 h-1 bg-background-950/60"><div className="h-full bg-[#F19744]" style={{ width: `${progress}%` }} /></div>}
                      {activeView === 'recent' && finished && <span className="absolute top-1.5 left-1.5 text-[9px] font-bold text-background-950 bg-accent-400 px-1 py-0.5 rounded whitespace-nowrap">{t('cinema_finished')}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm md:text-base font-semibold text-foreground-100 font-heading truncate">{item.title}</h3>
                      <div className="flex items-center gap-2 mt-1 text-[11px] md:text-xs text-foreground-500 flex-wrap"><span>{item.category}</span><span className="w-0.5 h-0.5 rounded-full bg-foreground-700" /><span>{item.totalEpisodes} {t('cinema_episodesUnit')}</span></div>
                      <div className="mt-2 text-[10px] md:text-[11px] text-foreground-600">{activeView === 'recent' ? (finished ? t('cinema_watchedFull') : t('cinema_lastWatched', { episode: item.lastEpisodeNo || 1 })) : t('cinema_purchasedEpisodes', { count: item.unlockedCount })}</div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      {canRemove && <button onClick={(event) => { event.stopPropagation(); handleRemove(item); }} className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-600 hover:text-red-400 hover:bg-background-800 transition-all opacity-100 md:opacity-0 md:group-hover:opacity-100 cursor-pointer"><i className={activeView === 'favorites' ? 'ri-bookmark-line' : 'ri-delete-bin-line'} /></button>}
                      <span className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium text-background-950 bg-[#F19744] whitespace-nowrap"><i className="ri-play-fill" />{activeView === 'recent' && !finished ? t('cinema_continueWatch') : t('cinema_play')}</span>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
